/// <reference types="vite/client" />
import axios from "axios";
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { emitToastError } from "../utils/toastBridge";

declare module "axios" {
  interface AxiosRequestConfig {
    suppressErrorToast?: boolean;
  }
}

const client: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
});

function errorMessage(err: AxiosError): string {
  if (!err.response) return "Network error — please check your connection.";
  const detail = (err.response.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && typeof detail[0]?.msg === "string") return detail[0].msg;
  return "Something went wrong. Please try again.";
}

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers["X-User-Timezone"] =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return config;
});

// Separate instance for token refresh — bypasses the response interceptor to
// avoid infinite retry loops.
const _refreshClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
});

type QueueEntry = { resolve: (token: string) => void; reject: (error: unknown) => void };
type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let _isRefreshing = false;
let _queue: QueueEntry[] = [];

function _flushQueue(token: string | null, error: unknown) {
  _queue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token as string)));
  _queue = [];
}

client.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as RetriableConfig | undefined;
    // Don't attempt refresh for auth endpoints or if we already retried
    if (
      err.response?.status !== 401 ||
      !original ||
      original._retry ||
      original.url?.includes("/auth/login") ||
      original.url?.includes("/auth/refresh")
    ) {
      if (err.response?.status === 401) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/login";
      } else if (
        !original?.suppressErrorToast &&
        !axios.isCancel(err) &&
        (original?.method ?? "get").toLowerCase() !== "get" &&
        !original?.url?.includes("/auth/")
      ) {
        emitToastError(errorMessage(err));
      }
      return Promise.reject(err);
    }

    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      localStorage.removeItem("access_token");
      window.location.href = "/login";
      return Promise.reject(err);
    }

    if (_isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        _queue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return client(original);
      });
    }

    original._retry = true;
    _isRefreshing = true;

    try {
      const res = await _refreshClient.post("/auth/refresh", {
        refresh_token: refreshToken,
      });
      const { access_token, refresh_token: newRefresh } = res.data;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", newRefresh);
      client.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      _flushQueue(access_token, null);
      original.headers.Authorization = `Bearer ${access_token}`;
      return client(original);
    } catch (refreshErr) {
      _flushQueue(null, refreshErr);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      window.location.href = "/login";
      return Promise.reject(refreshErr);
    } finally {
      _isRefreshing = false;
    }
  }
);

export default client;
